// Pure speaker-diarization maths: turn assembly, embedding clustering, and
// mapping speakers onto transcript segments.
//
// Kept free of any model import so it can be unit-tested without downloading
// 30 MB of ONNX weights — diarize.js supplies the embeddings, this decides who
// is who.

/** Cosine distance in [0, 2]. Embeddings are usually L2-normalised, but don't assume it. */
export function cosineDistance(a, b) {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  const denom = Math.sqrt(na) * Math.sqrt(nb);
  return denom ? 1 - dot / denom : 1;
}

/**
 * Agglomerative clustering with average linkage.
 *
 * Two modes, because they fail differently:
 *   - `numSpeakers` given: merge until exactly that many clusters remain. Far
 *     more reliable, and an investigator usually knows it's a two-person call.
 *   - auto: merge while the closest pair is nearer than `threshold`. Tends to
 *     over-split on noisy phone audio, which is why the UI asks for a count.
 *
 * @param {ArrayLike<number>[]} embeddings
 * @returns {number[]} cluster index per embedding, numbered by first appearance
 */
export function agglomerativeCluster(embeddings, { numSpeakers = null, threshold = 0.65 } = {}) {
  const n = embeddings.length;
  if (n === 0) return [];
  if (n === 1) return [0];

  const dist = Array.from({ length: n }, () => new Float64Array(n));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = cosineDistance(embeddings[i], embeddings[j]);
      dist[i][j] = d;
      dist[j][i] = d;
    }
  }

  let clusters = embeddings.map((_, i) => [i]);

  const linkage = (a, b) => {
    let sum = 0;
    for (const i of a) for (const j of b) sum += dist[i][j];
    return sum / (a.length * b.length);
  };

  const target = numSpeakers ? Math.max(1, Math.min(numSpeakers, n)) : 1;

  while (clusters.length > target) {
    let best = Infinity;
    let bi = -1;
    let bj = -1;

    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const d = linkage(clusters[i], clusters[j]);
        if (d < best) { best = d; bi = i; bj = j; }
      }
    }

    if (bi < 0) break;
    // In auto mode, stop once the nearest pair is too far apart to be one voice.
    if (!numSpeakers && best > threshold) break;

    clusters[bi] = clusters[bi].concat(clusters[bj]);
    clusters.splice(bj, 1);
  }

  // Number speakers by when they first talk, so "Speaker 1" opens the recording.
  const labels = new Array(n).fill(0);
  clusters
    .map(c => ({ c, first: Math.min(...c) }))
    .sort((a, b) => a.first - b.first)
    .forEach(({ c }, idx) => { for (const i of c) labels[i] = idx; });

  return labels;
}

/**
 * Collapse raw frame-level segmentation output into speech turns.
 *
 * pyannote's powerset classes are per-window and not stable across windows, so
 * they are only used here to split at speaker changes *inside* a window —
 * global identity comes from clustering the embeddings afterwards.
 *
 * @param {{start:number, end:number, id:number, confidence?:number}[]} segments
 */
export function mergeTurns(segments, { silenceClass = 0, minDuration = 0.25, maxGap = 0.15 } = {}) {
  const speech = segments
    .filter(s => s.id !== silenceClass && s.end > s.start)
    .sort((a, b) => a.start - b.start);

  const turns = [];
  for (const seg of speech) {
    const prev = turns[turns.length - 1];
    // Same local speaker and nearly touching → one continuous turn.
    if (prev && seg.id === prev.id && seg.start - prev.end <= maxGap) {
      prev.end = Math.max(prev.end, seg.end);
    } else {
      turns.push({ start: seg.start, end: seg.end, id: seg.id });
    }
  }

  return turns.filter(t => t.end - t.start >= minDuration);
}

/** Overlap in seconds between two [start, end) intervals. */
export function overlapSeconds(a, b) {
  return Math.max(0, Math.min(a.end, b.end) - Math.max(a.start, b.start));
}

/**
 * Label each transcript segment with the speaker whose turn covers most of it.
 *
 * @param {{start:number|null, end:number|null, text:string}[]} chunks
 * @param {{start:number, end:number, speaker:number}[]} turns
 * @returns {(number|null)[]} speaker index per chunk
 */
export function assignSpeakers(chunks, turns) {
  let previous = null;

  return chunks.map((chunk) => {
    const start = chunk.start ?? 0;
    const end = chunk.end ?? start;
    if (end <= start || !turns.length) return previous;

    const totals = new Map();
    for (const turn of turns) {
      const shared = overlapSeconds({ start, end }, turn);
      if (shared > 0) totals.set(turn.speaker, (totals.get(turn.speaker) || 0) + shared);
    }

    if (!totals.size) return previous;   // silence or music — carry the last voice

    let bestSpeaker = null;
    let bestShare = -1;
    for (const [speaker, shared] of totals) {
      if (shared > bestShare) { bestShare = shared; bestSpeaker = speaker; }
    }

    previous = bestSpeaker;
    return bestSpeaker;
  });
}

/** Default display names, overridable by the user. */
export function defaultSpeakerLabel(index) {
  return `Speaker ${index + 1}`;
}

// Some call-recording systems (GTL/ICMV included) open with a single
// automated announcement — a "this call is from a correctional facility",
// consent-to-record, or rate notice — played identically on BOTH channels
// before the two parties' audio actually diverges. Transcribed
// independently, that produces two near-identical chunks at near-identical
// timestamps, which read as a doubled line once merged. dedupeSimultaneousChunks
// collapses those into one unattributed line rather than crediting a
// recorded system message to either party.

const DUPLICATE_MIN_WORDS = 4;            // below this, treat matches as coincidence (e.g. both saying "okay")
const DUPLICATE_TEXT_SIMILARITY = 0.8;    // Jaccard over words, after normalizing case/punctuation
const DUPLICATE_MIN_OVERLAP_FRACTION = 0.5; // of the shorter chunk's duration
const DUPLICATE_MAX_GAP = 2;              // seconds — bounds the search window on the sorted list

function normalizeWords(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
}

/** Jaccard similarity over word sets — cheap, order-insensitive, tolerant of
 *  the minor wording differences two independent transcription passes of the
 *  same audio can occasionally produce. */
function wordSimilarity(a, b) {
  const wordsA = normalizeWords(a);
  const wordsB = normalizeWords(b);
  if (!wordsA.length || !wordsB.length) return 0;

  const setA = new Set(wordsA);
  const setB = new Set(wordsB);
  let intersection = 0;
  for (const w of setA) if (setB.has(w)) intersection++;
  const union = new Set([...setA, ...setB]).size;
  return intersection / union;
}

/**
 * Drop one side of any pair of chunks from DIFFERENT channels that overlap
 * in time and say essentially the same thing — evidence of shared/system
 * audio duplicated onto both channels, not two parties talking. The
 * surviving chunk is marked unattributed (speaker: null) rather than
 * credited to whichever channel happened to come first, reusing the same
 * "Unlabelled" rendering the transcript already has for ML diarization gaps.
 *
 * @param {{start:number|null, end:number|null, text:string, speaker:number}[]} tagged sorted by start
 */
function dedupeSimultaneousChunks(tagged) {
  const drop = new Set();

  for (let i = 0; i < tagged.length; i++) {
    if (drop.has(i)) continue;
    const a = tagged[i];
    const aStart = a.start ?? 0;
    const aEnd = a.end ?? aStart;
    if (aEnd <= aStart) continue;

    for (let j = i + 1; j < tagged.length; j++) {
      const b = tagged[j];
      const bStart = b.start ?? 0;
      if (bStart > aEnd + DUPLICATE_MAX_GAP) break; // sorted by start — nothing further can overlap
      if (drop.has(j) || b.speaker === a.speaker) continue;

      const bEnd = b.end ?? bStart;
      if (bEnd <= bStart) continue;

      const shared = overlapSeconds({ start: aStart, end: aEnd }, { start: bStart, end: bEnd });
      const shorter = Math.min(aEnd - aStart, bEnd - bStart);
      if (shared / shorter < DUPLICATE_MIN_OVERLAP_FRACTION) continue;

      if (normalizeWords(a.text).length < DUPLICATE_MIN_WORDS || normalizeWords(b.text).length < DUPLICATE_MIN_WORDS) continue;
      if (wordSimilarity(a.text, b.text) < DUPLICATE_TEXT_SIMILARITY) continue;

      a.speaker = null;
      drop.add(j);
    }
  }

  return tagged.filter((_, idx) => !drop.has(idx));
}

/**
 * Merge independently-transcribed channels into one time-ordered chunk
 * stream, each chunk tagged with the channel it came from. Unlike
 * assignSpeakers (which infers a speaker from ML-diarization turns by time
 * overlap against a single mixed-down transcript), each channel here was
 * already transcribed on its own — there's no inference for real speech,
 * just tag and sort. The one exception is shared/system audio duplicated
 * onto both channels (see dedupeSimultaneousChunks above).
 *
 * @param {{ chunks: {start:number|null, end:number|null, text:string}[] }[]} resultsByChannel
 * @returns {{ chunks: {start,end,text}[], speakerOf: (number|null)[], text: string }}
 */
export function mergeChannelTranscripts(resultsByChannel) {
  const tagged = resultsByChannel
    .flatMap((result, channelIdx) => result.chunks.map(c => ({ ...c, speaker: channelIdx })))
    .sort((a, b) => (a.start ?? 0) - (b.start ?? 0));

  const deduped = dedupeSimultaneousChunks(tagged);

  return {
    chunks: deduped.map(({ speaker, ...chunk }) => chunk),
    speakerOf: deduped.map(t => t.speaker),
    text: deduped.map(c => c.text).join(' '),
  };
}

/** Default label for a channel-split speaker — always exactly 2 channels
 *  (see Transcribe.js), so this is deliberately Left/Right, not numbered-generic. */
export function defaultChannelSpeakerLabel(idx) {
  return idx === 0 ? 'Speaker 1 (Left channel)' : 'Speaker 2 (Right channel)';
}
